import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Save, X, Plus, Image } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminProductForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, token } = useAuth();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    category: "clothing",
    subcategory: "",
    image_url: "",
    sizes: [],
    colors: [],
    stock: "",
    is_new_arrival: false,
    is_featured: false
  });
  const [newSize, setNewSize] = useState("");
  const [newColor, setNewColor] = useState("");

  useEffect(() => {
    if (!user?.is_admin) {
      navigate("/");
      return;
    }

    if (isEdit) {
      const fetchProduct = async () => {
        try {
          const response = await axios.get(`${API}/products/${id}`);
          const product = response.data;
          setFormData({
            name: product.name,
            description: product.description,
            price: product.price.toString(),
            category: product.category,
            subcategory: product.subcategory || "",
            image_url: product.image_url,
            sizes: product.sizes || [],
            colors: product.colors || [],
            stock: product.stock.toString(),
            is_new_arrival: product.is_new_arrival,
            is_featured: product.is_featured
          });
        } catch (error) {
          toast.error("Product not found");
          navigate("/admin/products");
        } finally {
          setLoading(false);
        }
      };
      fetchProduct();
    }
  }, [user, id, isEdit, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.price || !formData.image_url) {
      toast.error("Please fill in required fields");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock) || 0,
        subcategory: formData.subcategory || null
      };

      if (isEdit) {
        await axios.put(`${API}/products/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Product updated!");
      } else {
        await axios.post(`${API}/products`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Product created!");
      }
      navigate("/admin/products");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  const addSize = () => {
    if (newSize && !formData.sizes.includes(newSize)) {
      setFormData(prev => ({ ...prev, sizes: [...prev.sizes, newSize] }));
      setNewSize("");
    }
  };

  const removeSize = (size) => {
    setFormData(prev => ({ ...prev, sizes: prev.sizes.filter(s => s !== size) }));
  };

  const addColor = () => {
    if (newColor && !formData.colors.includes(newColor)) {
      setFormData(prev => ({ ...prev, colors: [...prev.colors, newColor] }));
      setNewColor("");
    }
  };

  const removeColor = (color) => {
    setFormData(prev => ({ ...prev, colors: prev.colors.filter(c => c !== color) }));
  };

  if (!user?.is_admin) return null;

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-24 px-4 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#39FF14] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-24 px-4 sm:px-6 lg:px-8" data-testid="admin-product-form">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/admin/products"
            className="flex items-center gap-2 text-[#888] hover:text-[#39FF14] transition-colors mb-4"
          >
            <ArrowLeft size={18} />
            <span className="label-text">Back to Products</span>
          </Link>
          <h1 className="text-4xl heading text-[#EDEDED]">
            {isEdit ? "Edit Product" : "Add New Product"}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <div className="bg-[#0A0A0A] border border-[#222] p-6">
            <h3 className="text-xl heading text-[#EDEDED] mb-6">Basic Information</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-[#888] label-text">Product Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-2 gcsr-input w-full"
                  placeholder="GCSR Midnight Hoodie"
                  required
                  data-testid="product-name"
                />
              </div>

              <div>
                <Label htmlFor="description" className="text-[#888] label-text">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="mt-2 gcsr-input w-full min-h-[100px] resize-none"
                  placeholder="Premium heavyweight hoodie with reflective GCSR logo..."
                  data-testid="product-description"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price" className="text-[#888] label-text">Price *</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                    className="mt-2 gcsr-input w-full"
                    placeholder="89.99"
                    required
                    data-testid="product-price"
                  />
                </div>

                <div>
                  <Label htmlFor="stock" className="text-[#888] label-text">Stock</Label>
                  <Input
                    id="stock"
                    type="number"
                    min="0"
                    value={formData.stock}
                    onChange={(e) => setFormData(prev => ({ ...prev, stock: e.target.value }))}
                    className="mt-2 gcsr-input w-full"
                    placeholder="50"
                    data-testid="product-stock"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#888] label-text">Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger className="mt-2 bg-transparent border-[#333] text-[#EDEDED]" data-testid="product-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-[#333]">
                      <SelectItem value="clothing" className="text-[#EDEDED] focus:bg-[#1a1a1a] focus:text-[#39FF14]">Clothing</SelectItem>
                      <SelectItem value="accessories" className="text-[#EDEDED] focus:bg-[#1a1a1a] focus:text-[#39FF14]">Accessories</SelectItem>
                      <SelectItem value="apparel" className="text-[#EDEDED] focus:bg-[#1a1a1a] focus:text-[#39FF14]">Apparel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="subcategory" className="text-[#888] label-text">Subcategory</Label>
                  <Input
                    id="subcategory"
                    value={formData.subcategory}
                    onChange={(e) => setFormData(prev => ({ ...prev, subcategory: e.target.value }))}
                    className="mt-2 gcsr-input w-full"
                    placeholder="hoodies, tees, hats..."
                    data-testid="product-subcategory"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="bg-[#0A0A0A] border border-[#222] p-6">
            <h3 className="text-xl heading text-[#EDEDED] mb-6">Product Image</h3>
            <div>
              <Label htmlFor="image_url" className="text-[#888] label-text">Image URL *</Label>
              <Input
                id="image_url"
                value={formData.image_url}
                onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                className="mt-2 gcsr-input w-full"
                placeholder="https://images.unsplash.com/..."
                required
                data-testid="product-image"
              />
              {formData.image_url && (
                <div className="mt-4 relative w-48 h-48">
                  <img
                    src={formData.image_url}
                    alt="Preview"
                    className="w-full h-full object-cover border border-[#333]"
                    onError={(e) => { e.target.style.display = 'none' }}
                  />
                </div>
              )}
              {!formData.image_url && (
                <div className="mt-4 w-48 h-48 border border-dashed border-[#333] flex items-center justify-center">
                  <Image size={32} className="text-[#333]" />
                </div>
              )}
            </div>
          </div>

          {/* Sizes */}
          <div className="bg-[#0A0A0A] border border-[#222] p-6">
            <h3 className="text-xl heading text-[#EDEDED] mb-6">Sizes</h3>
            <div className="flex flex-wrap gap-2 mb-4">
              {formData.sizes.map((size) => (
                <span
                  key={size}
                  className="flex items-center gap-2 px-3 py-1 bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/30"
                >
                  {size}
                  <button type="button" onClick={() => removeSize(size)} className="hover:text-white">
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newSize}
                onChange={(e) => setNewSize(e.target.value)}
                className="gcsr-input flex-1"
                placeholder="Add size (S, M, L, XL...)"
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSize())}
                data-testid="add-size-input"
              />
              <button
                type="button"
                onClick={addSize}
                className="px-4 py-2 border border-[#333] text-[#EDEDED] hover:border-[#39FF14] hover:text-[#39FF14] transition-colors"
                data-testid="add-size-btn"
              >
                <Plus size={20} />
              </button>
            </div>
          </div>

          {/* Colors */}
          <div className="bg-[#0A0A0A] border border-[#222] p-6">
            <h3 className="text-xl heading text-[#EDEDED] mb-6">Colors</h3>
            <div className="flex flex-wrap gap-2 mb-4">
              {formData.colors.map((color) => (
                <span
                  key={color}
                  className="flex items-center gap-2 px-3 py-1 bg-[#FF00FF]/10 text-[#FF00FF] border border-[#FF00FF]/30"
                >
                  {color}
                  <button type="button" onClick={() => removeColor(color)} className="hover:text-white">
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newColor}
                onChange={(e) => setNewColor(e.target.value)}
                className="gcsr-input flex-1"
                placeholder="Add color (Black, White, Neon Green...)"
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addColor())}
                data-testid="add-color-input"
              />
              <button
                type="button"
                onClick={addColor}
                className="px-4 py-2 border border-[#333] text-[#EDEDED] hover:border-[#FF00FF] hover:text-[#FF00FF] transition-colors"
                data-testid="add-color-btn"
              >
                <Plus size={20} />
              </button>
            </div>
          </div>

          {/* Status */}
          <div className="bg-[#0A0A0A] border border-[#222] p-6">
            <h3 className="text-xl heading text-[#EDEDED] mb-6">Product Status</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="is_new_arrival"
                  checked={formData.is_new_arrival}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_new_arrival: checked }))}
                  className="border-[#333] data-[state=checked]:bg-[#39FF14] data-[state=checked]:border-[#39FF14]"
                  data-testid="is-new-arrival"
                />
                <label htmlFor="is_new_arrival" className="text-[#EDEDED] cursor-pointer">
                  Mark as New Arrival
                </label>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="is_featured"
                  checked={formData.is_featured}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_featured: checked }))}
                  className="border-[#333] data-[state=checked]:bg-[#FF00FF] data-[state=checked]:border-[#FF00FF]"
                  data-testid="is-featured"
                />
                <label htmlFor="is_featured" className="text-[#EDEDED] cursor-pointer">
                  Mark as Featured
                </label>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 btn-skew bg-[#39FF14] text-black px-8 py-4 font-bold uppercase tracking-wider flex items-center justify-center gap-3 disabled:opacity-50"
              data-testid="save-product-btn"
            >
              <Save size={20} className="transform skewX-[12deg]" />
              <span>{saving ? "Saving..." : isEdit ? "Update Product" : "Create Product"}</span>
            </button>
            <Link
              to="/admin/products"
              className="btn-skew border border-[#333] text-[#EDEDED] px-8 py-4 font-bold uppercase hover:border-[#888]"
            >
              <span>Cancel</span>
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
